import { useMemo } from "react"
import { useLicenseQuery } from "@/v2-backoffice-client/license/license.query"
import { isLicenseLive } from "@/v2-backoffice-client/license/license.utils"
import { useLicenseSeatHoldersQuery } from "@/v2-backoffice-client/license/seats/license.seats.holders.query"
import {
  userLicenseSeatsOf,
  type UserLicenseSeat,
} from "@/v2-backoffice-client/license/seats/license.seats.utils"

type UserLike = {
  Id: number
  CustomerId?: number | null
}

/**
 * Where one user stands on each seated license of the customer that owns
 * them (the MCP servers): seat held, no seat, or a license that reaches
 * everyone. Composed from the customer's licenses (the Licenses & add-ons
 * tab's read) and its seat holders, which a grant or revoke evicts, so the
 * answer follows the change. Empty for a user nobody owns, for a customer
 * without a live seated license, and until the holders are known, so a
 * held seat never shows as missing first.
 */
export function useUserLicenseSeats(
  user: UserLike | null | undefined,
): UserLicenseSeat[] {
  const customerId = user?.CustomerId ?? null

  const { data: licenseData } = useLicenseQuery({
    variables: { customerId: customerId ?? 0 },
    skip: customerId === null,
  })
  const seatedLicenses = useMemo(
    () =>
      (licenseData?.getLicenses ?? []).filter(
        (license) => license.SeatsAreAssigned && isLicenseLive(license),
      ),
    [licenseData],
  )
  // Only a limited license names people; a seated license in the Everyone
  // mode has no holders to read.
  const needsHolders = seatedLicenses.some((license) => license.SeatsRestricted)

  const { data: holdersData } = useLicenseSeatHoldersQuery({
    variables: { customerId: customerId ?? 0 },
    skip: customerId === null || !needsHolders,
  })
  const holders = holdersData?.getLicenseSeatHolders

  return useMemo(() => {
    if (!user || (needsHolders && !holders)) return []
    return userLicenseSeatsOf(seatedLicenses, holders ?? [], user.Id)
  }, [user, seatedLicenses, needsHolders, holders])
}
